<template>
  <b-container
    style="min-height: calc(100vh - 135px); max-height: calc(100vh - 135px); overflow: hidden;"
    fluid
    class="px-0 d-flex flex-direction gap-4"
  >
    <div
      id="comment-scroll-area"
      class="d-flex flex-column gap-y-3 pb-3 pt-2 border px-md-3"
    >
      <div
        v-if="comments.length <= 0 && !state.busy"
        class="mt-auto"
      >
        <div class="text-center py-4">
          No comments yet. Be the first to comment on this payment request.
        </div>
      </div>

      <template
        v-else-if="state.busy"
      >
        <div
          v-for="index in 6"
          :key="index"
          class="pb-1 px-2"
        >
          <MyCommentSkeleton
            v-if="index % 3 === 1"
            class="ml-auto"
            style="max-width: 80%;"
          />
          <OtherCommentSkeleton
            v-else
            class="mr-auto"
            style="max-width: 80%;"
          />
        </div>
      </template>

      <template
        v-else
      >
        <div
          v-for="(comment, index) in comments"
          :id="`comment-${comment.id}`"
          :key="index"
          :class="[
            'p-2', {
              'ml-auto': Number(comment.commenter_id) === Number(currentUser.id),
              'mr-auto': Number(comment.commenter_id) !== Number(currentUser.id)
            }
          ]"
          style="max-width: 80%;"
        >
          <MyComment
            v-if="Number(comment.commenter_id) === Number(currentUser.id)"
            :comment="comment"
            :comments="comments"
            :reply="formComment.thread_id"
            :busy="state.busy || state.sending"
            @delete:comment="deleteComment"
            @reply:comment="replyComment"
            @edit:comment="editComment"
          />
          <OtherComment
            v-else
            :comment="comment"
            :comments="comments"
            :reply="formComment.thread_id"
            :busy="state.busy || state.sending"
            @reply:comment="replyComment"
          />
        </div>
      </template>

      <b-button
        class="btn-comment-scroll-bottom text-primary"
        @click.prevent.stop="() => scrollToBottom()"
      >
        <i class="fat fa-arrow-down m-0 p-0" />
      </b-button>

      <div />
    </div>

    <div
      id="comment-input-area"
    >
      <div
        class="d-flex gap-2 align-items-start"
      >
        <div class="flex-grow-1">
          <ValidationObserver
            ref="formComment"
          >
            <ValidationProvider
              v-slot="{ errors }"
              rules="required|max:1000"
              name="comment"
              vid="comment"
            >
              <b-form-group
                label="Comment"
                label-for="comment"
                label-class="font-weight-bolder"
              >
                <template #label>
                  <div class="d-flex justify-content-between">
                    <div>Comment</div>
                    <div>{{ String(formComment.comment || '').length }} / 1000</div>
                  </div>
                </template>
                <b-form-textarea
                  id="comment"
                  ref="inputComment"
                  v-model="formComment.comment"
                  rows="4"
                  max-rows="24"
                  placeholder="Compose here . . ."
                  :state="(errors.length > 0 ? false : null)"
                  :readonly="state.busy || state.sending"
                  @keydown.shift.enter="validateComment"
                />
                <!-- <div
                    v-if="errors.length > 0"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  /> -->
              </b-form-group>
            </ValidationProvider>
          </ValidationObserver>
        </div>

        <div
          class="d-flex flex-column gap-2"
          style="margin-top: 34.2px"
        >
          <!-- <b-button
            size="sm"
            variant="dark"
            :disabled="state.busy || state.sending"
          >
            <i class="ri-attachment-line m-0 p-0" />
          </b-button> -->

          <b-button
            size="sm"
            :variant="state.replying ? 'secondary' : 'primary'"
            :disabled="state.busy || state.sending"
            @click.prevent.stop="validateComment"
          >
            <i
              v-if="state.sending"
              class="fat fa-spinner-third fa-spin m-0 p-0"
            />
            <i
              v-else
              class="ri-send-plane-line m-0 p-0"
            />
            <!-- {{ state.sending ? (state.editing ? 'Updating...' : 'Sending...') : (state.editing ? 'Update' : (state.replying ? 'Reply' : 'Send')) }} -->
          </b-button>

          <b-button
            v-if="formComment.id"
            size="sm"
            variant="outline-danger"
            :disabled="state.busy || state.sending"
            @click.prevent.stop="discardEditComment"
          >
            <i class="ri-close-line m-0 p-0" />
            <!-- Discard -->
          </b-button>
          <b-button
            v-else-if="formComment.thread_id"
            size="sm"
            variant="outline-danger"
            :disabled="state.busy || state.sending"
            @click.prevent.stop="discardReplyComment"
          >
            <i class="ri-close-line m-0 p-0" />
            <!-- Discard -->
          </b-button>
        </div>
      </div>
    </div>
  </b-container>
</template>
<script>
import { mapGetters } from 'vuex'

import MyComment from '@/components/custom/comments/MyComment.vue'
import MyCommentSkeleton from '@/components/custom/comments/MyCommentSkeleton.vue'
import OtherComment from '@/components/custom/comments/OtherComment.vue'
import OtherCommentSkeleton from '@/components/custom/comments/OtherCommentSkeleton.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import { SRAMPaymentComment } from '@/services'

export default {
  name: 'CommentBase',

  components: {
    MyComment,
    MyCommentSkeleton,
    OtherComment,
    OtherCommentSkeleton
  },

  mixins: [
    MxMisc,
    MxFormatter
  ],

  props: {
    paymentRequest: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      state: {
        busy: false,
        editing: false,
        replying: false,
        sending: false
      },
      comments: [],
      formComment: {
        id: '',
        thread_id: '',
        comment: ''
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    })
  },

  mounted () {
    if (this.currentUser) {
      this.gridProvider()
      this.initAutoScrollBottom()
      this.initCommentScrollBottom()
    }
  },

  destroyed () {
    const commentScrollArea = document.getElementById('comment-scroll-area')

    if (commentScrollArea) {
      commentScrollArea.removeEventListener('scroll', null)
    }
  },

  methods: {

    initCommentScrollBottom () {
      const commentScrollArea = document.getElementById('comment-scroll-area')

      if (commentScrollArea) {
        commentScrollArea.addEventListener('scroll', () => {
          const scrollBottomButton = document.querySelector('.btn-comment-scroll-bottom')

          const nearBottom = (
            commentScrollArea.scrollHeight -
            commentScrollArea.scrollTop -
            commentScrollArea.clientHeight
          )

          scrollBottomButton.classList.toggle('show', !(nearBottom < 50))
        })
      }
    },

    scrollToBottom (behavior = 'smooth') {
      const commentScrollArea = document.getElementById('comment-scroll-area')

      if (commentScrollArea) {
        const lastChild = commentScrollArea.lastElementChild
        if (lastChild) {
          commentScrollArea.scrollTo({
            top: Number(lastChild.offsetTop) + Number(lastChild.offsetHeight),
            behavior
          })
        } else {
          commentScrollArea.scrollTo({
            top: commentScrollArea.scrollHeight,
            behavior
          })
        }
      }
    },

    initAutoScrollBottom () {
      const commentScrollArea = document.getElementById('comment-scroll-area')

      const observer = new MutationObserver(() => {
        this.scrollToBottom('instant')
      })

      observer.observe(commentScrollArea, {
        childList: true
        // subtree: true
      })
    },

    async gridProvider (noLoading = false) {
      if (!noLoading) {
        this.state.busy = true
      }

      return SRAMPaymentComment.get(this.paymentRequest.id).then(
        ({ data }) => {
          this.comments = data.comments

          return data.comments
        }
      ).catch(() => {
        this.comments = []
        return []
      }).finally(() => {
        if (!noLoading) {
          this.state.busy = false
        }
      })
    },

    findComment (id) {
      return this.comments.find(
        comment => String(comment.id) === String(id)
      )
    },

    replyComment (comment) {
      this.state.editing = false
      this.state.replying = true

      Object.assign(this.formComment, {
        id: '',
        thread_id: comment.id,
        comment: ''
      })

      this.$refs.inputComment.focus()
    },

    editComment (comment) {
      this.state.editing = true
      this.state.replying = false

      Object.assign(this.formComment, {
        id: comment.id,
        thread_id: comment.thread_id,
        comment: comment.comment
      })

      this.$refs.inputComment.focus()
    },

    discardEditComment () {
      this.state.editing = false

      Object.assign(this.formComment, {
        id: '',
        thread_id: '',
        comment: ''
      })
    },

    discardReplyComment () {
      this.state.replying = false

      Object.assign(this.formComment, {
        id: '',
        thread_id: '',
        comment: ''
      })
    },

    validateComment () {
      if (this.state.busy || this.state.sending) {
        return
      }

      this.$refs.formComment.validate().then(
        async validComment => {
          if (!validComment) {
            return this.swalInvalid()
          }

          Object.assign(this.formComment, {
            comment: String(this.formComment.comment || '')
          })

          this.state.sending = true

          const createOrUpdate = () => {
            return this.state.editing ? this.putComment() : this.postComment()
          }

          return createOrUpdate().catch(
            ({ message }) => {
              if (message && message?.errors) {
                this.$refs.formComment.setErrors(message.errors)
              }
            }
          ).finally(() => {
            this.state.sending = false
            this.$refs.formComment.reset()
          })

          // return this.swalConfirm({
          //   title: this.state.editing ? 'Update Comment' : 'Send Comment',
          //   html: this.state.editing ? 'Update your comment?' : 'Send this comment?',
          //   confirmButtonText: this.state.editing ? 'Update' : 'Send',
          //   cancelButtonText: 'Discard',
          //   preConfirm: async () => {
          //   }
          // })
        }
      )
    },

    async postComment () {
      return SRAMPaymentComment.post(this.paymentRequest.id, this.formComment).then(
        ({ data }) => {
          this.gridProvider(true)

          // this.swalSuccess(data.message).then(() => {
          // })

          Object.assign(this.formComment, {
            id: '',
            thread_id: '',
            comment: ''
          })

          return data
        }
      )
    },

    async putComment () {
      return SRAMPaymentComment.put(this.paymentRequest.id, this.formComment).then(
        ({ data }) => {
          this.gridProvider(true)

          // this.swalSuccess(data.message).then(() => {
          // })

          const commentIndex = this.comments.findIndex(
            comment => String(comment.id) === String(this.formComment.id)
          )

          if (commentIndex >= 0) {
            this.comments[commentIndex] = data.comment
          }

          Object.assign(this.formComment, {
            id: '',
            thread_id: '',
            comment: ''
          })

          return data
        }
      )
    },

    async deleteComment (comment) {
      return this.swalConfirm({
        title: 'Delete Comment',
        html: 'Delete this comment? This action cannot be undone.',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Discard',
        customClass: {
          confirmButton: 'btn btn-sm btn-danger'
        },
        preConfirm: async () => {
          const payload = {
            id: comment.id
          }

          return SRAMPaymentComment.delete(this.paymentRequest.id, payload).then(
            ({ data }) => {
              this.gridProvider(true)

              // this.swalSuccess(data.message).then(() => {
              // })

              const commentIndex = this.comments.findIndex(
                c => String(c.id) === String(comment.id)
              )

              if (commentIndex >= 0) {
                this.comments.splice(commentIndex, 1)
              }

              Object.assign(this.formComment, {
                id: '',
                thread_id: '',
                comment: ''
              })

              return data
            }
          )
        }
      })
    }
  }
}
</script>
<style scoped>
#comment-scroll-area {
  position: relative;
  scrollbar-width: thin;
  max-height: calc(100vh - 130px);
  overflow-y: auto;
  flex: 1 1 auto;
  min-height: 0;
  width: 65vw;
  margin-right: 0;
  margin-left: 0;
}

#comment-input-area {
  flex: 0 0 auto;
  width: 35vw;
}

#comment {
  max-height: calc(100vh - 170px);
}

.btn-comment-scroll-bottom {
  position: sticky;
  left: calc(65vw / 2 - 22px);
  width: 44px;
  border-radius: 50%;
  cursor: pointer;
  border: none;
  background: linear-gradient(180deg, rgba(242, 244, 239, 0.8) 0%, rgba(137, 247, 163, 0.6) 100%);
  opacity: 0;
  pointer-events: none;
  transform: translateY(10px);
  transition: .2s;
  bottom: 0;
  display: none;
}

.btn-comment-scroll-bottom.show {
  opacity: 1;
  display: block;
  pointer-events: auto;
  transform: translateY(0);
}

.flex-direction {
  flex-direction: row;
}
@media (max-width: 820px) {
  .flex-direction {
    flex-direction: column;
  }

  #comment-scroll-area {
    width: 100%;
  }

  #comment-input-area {
    width: 100%;
  }

  #comment {
    max-height: 30vh;
  }

  .btn-comment-scroll-bottom {
    left: calc(100vw / 2 - 22px);
  }
}
</style>
